/**
 * useTooltip Hook
 *
 * Manages seen-state for contextual tooltips using localStorage.
 * Each tooltip ID is tracked in a JSON string array under 'op_seenTooltips'.
 * Follows the same localStorage pattern as WelcomeScreen (OperatorLayout.tsx).
 *
 * Will be upgraded to database persistence in Phase 21.1.4.
 */

import { useState, useCallback } from 'react';

const STORAGE_KEY = 'op_seenTooltips';

function getSeenTooltips(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function markTooltipSeen(id: string): void {
  try {
    const seen = getSeenTooltips();
    if (!seen.includes(id)) {
      seen.push(id);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(seen));
    }
  } catch {
    // localStorage unavailable -- tooltip will reappear next visit, acceptable degradation
  }
}

export function useTooltip(id: string) {
  const [shouldShow, setShouldShow] = useState(() => !getSeenTooltips().includes(id));

  const dismiss = useCallback(() => {
    setShouldShow(false);
    markTooltipSeen(id);
  }, [id]);

  return { shouldShow, dismiss };
}
